import { ref, computed } from 'vue'
import { showToast } from '@/utils/toast'

// LocalStorage 键名
const STORAGE_KEYS = {
  MESSAGE_COUNT: 'rating_message_count',
  LAST_CANCEL_DATE: 'rating_last_cancel_date',
  HAS_RATED: 'rating_has_rated'
}

// 触发评分的消息数量阈值
const MESSAGE_THRESHOLD = 12

/**
 * 评分弹窗 Composable
 * 
 * 功能：
 * 1. 用户累计发送消息达到 12 次后，触发好评引导弹窗
 * 2. 点击"取消"：当日不再弹出，次日重新触发条件可再次弹出
 * 3. 评分为 5 星：自动跳转至应用商店，后续不再弹出
 * 4. 评分低于 5 星（1~4 星）：不跳转应用商店，提示"感谢您的评价"，后续不再弹出
 * 5. 用户完成评分（无论几星）后，永久不再触发好评引导
 */
export function useRatingPopup() {
  const showRatingPopup = ref(false)

  /**
   * 获取今天的日期字符串（YYYY-MM-DD）
   */
  const getTodayDate = (): string => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  }

  /**
   * 检查用户是否已经评分
   */
  const hasRated = (): boolean => {
    return localStorage.getItem(STORAGE_KEYS.HAS_RATED) === 'true'
  }

  /**
   * 检查今天是否已经取消过
   */
  const hasCanceledToday = (): boolean => {
    const lastCancelDate = localStorage.getItem(STORAGE_KEYS.LAST_CANCEL_DATE)
    return lastCancelDate === getTodayDate()
  }

  /**
   * 获取当前消息计数
   */
  const getMessageCount = (): number => {
    const count = localStorage.getItem(STORAGE_KEYS.MESSAGE_COUNT)
    return count ? parseInt(count, 10) : 0
  }

  /**
   * 设置消息计数
   */
  const setMessageCount = (count: number): void => {
    localStorage.setItem(STORAGE_KEYS.MESSAGE_COUNT, String(count))
  }
  const resetMessageCount = (): void => {
    localStorage.removeItem(STORAGE_KEYS.MESSAGE_COUNT)
  }

  /**
   * 增加消息计数
   */
  const incrementMessageCount = (): void => {
    const currentCount = getMessageCount()
    const newCount = currentCount + 1
    setMessageCount(newCount)

    // 检查是否应该弹出评分弹窗
    checkAndShowRating()
  }

  /**
   * 检查并显示评分弹窗
   */
  const checkAndShowRating = (): void => {
    return
    // 如果已经评分，不再弹出
    if (hasRated()) {
      return
    }

    // 如果今天已经取消过，不再弹出
    if (hasCanceledToday()) {
      return
    }

    // 如果消息数量达到阈值，显示弹窗
    const count = getMessageCount()
    if (count >= MESSAGE_THRESHOLD) {
      showRatingPopup.value = true
    }
  }

  /**
   * 处理用户评分
   */
  const handleRating = async (rating: number): Promise<void> => {
    // 标记为已评分，后续不再弹出
    localStorage.setItem(STORAGE_KEYS.HAS_RATED, 'true')

    if (rating === 5) {
      // 5 星评分：跳转到应用商店
      jumpToAppStore()
    } else {
      // 1~4 星评分：显示感谢提示
      showToast('Thank you for your feedback!', { type: 'success' })
    }

    // 关闭弹窗
    showRatingPopup.value = false
  }

  /**
   * 处理取消操作
   */
  const handleCancel = (): void => {
    // 记录今天取消的日期
    localStorage.setItem(STORAGE_KEYS.LAST_CANCEL_DATE, getTodayDate())
    
    // 关闭弹窗
    showRatingPopup.value = false
  }

  /**
   * 跳转到应用商店
   */
  const jumpToAppStore = (): void => {
    const userAgent = navigator.userAgent.toLowerCase()
    
    if (/iphone|ipad|ipod/.test(userAgent)) {
      // iOS 设备：跳转到 App Store
      // 替换为你的应用 App Store ID
      const appStoreUrl = 'https://apps.apple.com/app/idYOUR_APP_ID'
      window.location.href = appStoreUrl
    } else if (/android/.test(userAgent)) {
      // Android 设备：跳转到 Google Play
      // 替换为你的应用包名
      const playStoreUrl = 'https://play.google.com/store/apps/details?id=YOUR_PACKAGE_NAME'
      window.location.href = playStoreUrl
    } else {
      // 其他设备（如桌面浏览器）
      showToast('Please rate us on your mobile device', { type: 'warning' })
    }
  }

  /**
   * 重置评分状态（用于测试或特殊场景）
   */
  const resetRatingState = (): void => {
    localStorage.removeItem(STORAGE_KEYS.MESSAGE_COUNT)
    localStorage.removeItem(STORAGE_KEYS.LAST_CANCEL_DATE)
    localStorage.removeItem(STORAGE_KEYS.HAS_RATED)
  }

  // 计算属性
  const currentMessageCount = computed(() => getMessageCount())
  const isRated = computed(() => hasRated())

  return {
    showRatingPopup,
    currentMessageCount,
    isRated,
    getMessageCount,
    incrementMessageCount,
    handleRating,
    handleCancel,
    checkAndShowRating,
    resetRatingState,
    resetMessageCount
  }
}

